import React from "react";
import { Box, Typography, Button, Grid, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CameraAlt } from "@mui/icons-material";

const ImageSearchPromo = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Stagger variants for scroll animations
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: isMobile ? 0 : -40, y: isMobile ? 30 : 0 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const image1Variants = {
    hidden: { opacity: 0, scale: 0.8, rotate: -8, y: 40, x: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: -4,
      y: 0,
      x: 0,
      transition: { type: "spring", stiffness: 80, damping: 12 },
    }
  };

  const image2Variants = {
    hidden: { opacity: 0, scale: 0.8, rotate: 8, y: 60, x: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 4,
      y: 0,
      x: 0,
      transition: { type: "spring", stiffness: 80, damping: 12 },
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        pt: 0,
        pb: 0,
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 50%, #FFFFFF 100%)",
        borderTop: "1px solid #F3F3F3",
        overflow: "hidden"
      }}
    >
      <Box
        component={motion.div}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={containerVariants}
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: { xs: 2.5, sm: 4, md: 6 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 5, md: 8 }}
          alignItems="center"
          sx={{ flexDirection: { xs: "column", md: "row-reverse" } }}
        >
          {/* Right Side Copy (rendered first in DOM for animation order) */}
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              variants={textVariants}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "11px", md: "12px" },
                  color: "#FF6B00",
                  textTransform: "uppercase",
                  letterSpacing: "2.5px",
                  fontWeight: 600,
                  mb: 1.5,
                }}
              >
                Try Image Search
              </Typography>

              <Typography
                sx={{
                  fontFamily: "'Lora', serif",
                  fontSize: { xs: "28px", sm: "36px", md: "46px" },
                  fontWeight: 500,
                  color: "#111",
                  lineHeight: 1.15,
                  mb: 2.5,
                  letterSpacing: "-0.02em",
                }}
              >
                Find items from <br />
                <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>
                  a single screenshot.
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Poppins-Regular",
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#555",
                  lineHeight: 1.6,
                  maxWidth: "500px",
                  mb: 4,
                }}
              >
                Have a screenshot of an outfit you love, or a photo of a pattern you want to match? Upload it here, and we'll instantly scan top brand catalogs to find identical or visually similar items.
              </Typography>

              <Button
                onClick={() => navigate("/image-search")}
                variant="contained"
                disableElevation
                startIcon={<CameraAlt />}
                sx={{
                  borderRadius: "14px",
                  background: "#FF9B80", // Signature Peach
                  color: "#FFF",
                  px: 4,
                  py: 1.75,
                  fontSize: "15px",
                  fontFamily: "Poppins-Medium",
                  textTransform: "none",
                  boxShadow: "0 8px 24px rgba(255,155,128,0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "#ff8a6a",
                    boxShadow: "0 12px 30px rgba(255,155,128,0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Image Search
              </Button>
            </Box>
          </Grid>

          {/* Left Side: Staggered Image Cards */}
          <Grid item xs={12} md={7} style={{ width: "100%" }}>
            <Box
              component={motion.div}
              sx={{
                position: "relative",
                height: { xs: "280px", sm: "400px", md: "480px" },
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Screenshot 1 (Back left - Crop Tool) */}
              <Box
                component={motion.div}
                variants={image1Variants}
                className="promo-img-1"
                sx={{
                  position: "absolute",
                  width: { xs: "42%", sm: "42%" },
                  aspectRatio: "402/482",
                  left: "0",
                  top: "8%",
                  borderRadius: "12px",
                  border: { xs: "4px solid #FFFFFF", sm: "6px solid #FFFFFF" },
                  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                  backgroundColor: "#FFF",
                  zIndex: 1,
                }}
              >
                <img
                  src={require("../../assets/images/imagesearch_pic1.png")}
                  alt="Search Image Upload"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>

              {/* Screenshot 2 (Front right - Results) */}
              <Box
                component={motion.div}
                variants={image2Variants}
                className="promo-img-2"
                sx={{
                  position: "absolute",
                  width: { xs: "74%", sm: "74%" },
                  aspectRatio: "767/477",
                  right: "0",
                  bottom: "8%",
                  borderRadius: "12px",
                  border: { xs: "4px solid #FFFFFF", sm: "6px solid #FFFFFF" },
                  boxShadow: "0 14px 40px rgba(0,0,0,0.14)",
                  overflow: "hidden",
                  backgroundColor: "#FFF",
                  zIndex: 2,
                }}
              >
                <img
                  src={require("../../assets/images/imagesearch_pic2.png")}
                  alt="Search Image Results"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ImageSearchPromo;
